import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { useData } from "../hooks/useData";
import { clsx } from "./Primitives";

/**
 * Settings → Info site: administer info.saga-advisory.com from the CRM (admins only).
 * Everything goes through /api/admin/info/* — the CRM holds the key and calls the info site.
 */

type Row = Record<string, any>;
type Section = "overview" | "partners" | "desk" | "sources" | "review" | "briefing" | "scope" | "maintenance";
const SECTIONS: [Section, string][] = [
  ["overview", "Overview"], ["partners", "Partners"], ["desk", "Partner desk"], ["sources", "Sources"],
  ["review", "Review queue"], ["briefing", "Daily briefing"], ["scope", "Crawler scope"], ["maintenance", "Maintenance"],
];
const BASE = "/api/admin/info";
const INFO_PUBLIC = "https://info.saga-advisory.com";
const when = (iso?: string | null) => (iso ? new Date(iso.endsWith("Z") || /[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z")
  .toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function InfoAdmin() {
  const [sec, setSec] = useState<Section>("overview");
  return (
    <div className="ia">
      <div className="ia-nav">
        {SECTIONS.map(([id, label]) => (
          <button key={id} className={clsx("ia-nav-btn", sec === id && "on")} onClick={() => setSec(id)}>{label}</button>
        ))}
        <span className="ia-nav-note">Markets are under Settings → Markets</span>
      </div>
      {sec === "overview" && <Overview go={setSec} />}
      {sec === "partners" && <Partners />}
      {sec === "desk" && <Desk />}
      {sec === "sources" && <Sources />}
      {sec === "review" && <Review />}
      {sec === "briefing" && <Briefing />}
      {sec === "scope" && <Scope />}
      {sec === "maintenance" && <Maintenance />}
    </div>
  );
}

/* ---------- shared bits ---------- */

function useInfo<T = Row>(path: string | null) {
  const [err, setErr] = useState<{ message: string; retryable: boolean } | null>(null);
  const d = useData<T>(path);
  useEffect(() => {
    if (!d.error) { setErr(null); return; }
    setErr({ message: d.error, retryable: !/rejected the key|isn't set/.test(d.error) });
  }, [d.error]);
  return { ...d, err };
}

function Problem({ err, retry }: { err: { message: string; retryable: boolean } | string | null; retry?: () => void }) {
  if (!err) return null;
  const e = typeof err === "string" ? { message: err, retryable: !/rejected the key|isn't set|BW Offshore/.test(err) } : err;
  return (
    <div className="set-notice err ia-err">
      {e.message}
      {e.retryable && retry && <button className="btn ghost sm" onClick={retry}>Try again</button>}
    </div>
  );
}

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

function TagInput({ value, onChange, placeholder }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [t, setT] = useState("");
  const add = () => {
    const parts = t.split(/[,\n]/).map(x => x.trim()).filter(Boolean).filter(x => !value.includes(x));
    if (parts.length) onChange([...value, ...parts]);
    setT("");
  };
  return (
    <div className="ia-tags">
      {value.map(v => (
        <span key={v} className="ia-tag">{v}<button aria-label={`Remove ${v}`} onClick={() => onChange(value.filter(x => x !== v))}>×</button></span>
      ))}
      <input value={t} placeholder={placeholder || "Type and press Enter"} onChange={e => setT(e.target.value)} onBlur={add}
        onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } else if (e.key === "Backspace" && !t && value.length) onChange(value.slice(0, -1)); }} />
    </div>
  );
}

/* ---------- 1. Overview ---------- */

function Overview({ go }: { go: (s: Section) => void }) {
  const { data, err, refetch, loading } = useInfo<{ overview: Row; health: Row; warnings: string[] }>(`${BASE}/overview`);
  if (err) return <Problem err={err} retry={refetch} />;
  if (!data) return <div className="ia-loading">{loading ? "Reading the info site…" : ""}</div>;
  const o = data.overview;
  const Card = ({ title, children, to, warn }: { title: string; children: React.ReactNode; to?: Section; warn?: boolean }) => (
    <button className={clsx("ia-card", warn && "warn")} onClick={() => to && go(to)} disabled={!to}>
      <div className="ia-card-h">{title}</div>{children}
    </button>
  );
  const stale = o.pipeline?.last_crawl_at && Date.now() - new Date(o.pipeline.last_crawl_at + (/[Z+]/.test(o.pipeline.last_crawl_at.slice(-6)) ? "" : "Z")).getTime() > 8 * 3600_000;
  return (
    <>
      <div className={clsx("ia-health", data.health?.status === "ok" ? "ok" : "bad")}>
        <b>Info site: {data.health?.status || "unknown"}</b>
        {(data.health?.reasons || []).length > 0 && <span> — {(data.health.reasons as string[]).join(" · ")}</span>}
        <button className="btn ghost sm" onClick={refetch}>Refresh</button>
      </div>
      {data.warnings.length > 0 && <ul className="ia-warnings">{data.warnings.map(w => <li key={w}>{w}</li>)}</ul>}
      <div className="ia-cards">
        <Card title="Crawler" to="sources" warn={(o.pipeline?.last_crawl_failed || 0) > 0 || stale}>
          <div className="ia-big">{o.pipeline?.last_crawl_new_articles ?? "—"}<small> new last crawl</small></div>
          <div>Last crawl {when(o.pipeline?.last_crawl_at)} · {o.pipeline?.last_crawl_sources} sources · <b className={(o.pipeline?.last_crawl_failed || 0) > 0 ? "bad" : ""}>{o.pipeline?.last_crawl_failed} failed</b></div>
        </Card>
        <Card title="Sources" to="sources"><div className="ia-big">{o.sources?.enabled}<small> enabled</small></div><div>{o.sources?.disabled} disabled · {o.sources?.total} total</div></Card>
        <Card title="Articles"><div className="ia-big">{o.content?.articles?.toLocaleString?.() ?? o.content?.articles}</div>
          <div>{o.content?.articles_24h} in 24 h · {o.content?.tagged_pct}% tagged · {o.content?.missing_norwegian} missing Norwegian</div></Card>
        <Card title="Saga analyses"><div className="ia-big">{o.analyses?.published}<small> published</small></div><div>{o.analyses?.rejected} rejected · last {when(o.analyses?.last_generated_at)}</div></Card>
        <Card title="Partners" to="partners"><div className="ia-big">{o.partners?.active}<small> active</small></div><div>{o.partners?.total} profiles</div></Card>
        <Card title="Partner matches" to="desk"><div className="ia-big">{o.partner_matches_7d?.matches}<small> in 7 days</small></div></Card>
        <Card title="Review queue" to="review" warn={(o.review_queue?.pending || 0) > 50}><div className="ia-big">{o.review_queue?.pending}<small> pending</small></div></Card>
        <Card title="Sign-ups → CRM" warn={(o.crm_outbox?.failed || 0) > 0}>
          <div className="ia-big">{o.crm_outbox?.pending}<small> waiting</small></div>
          <div>{o.crm_outbox?.enabled ? "On" : "Off"} · <b className={(o.crm_outbox?.failed || 0) > 0 ? "bad" : ""}>{o.crm_outbox?.failed} failed</b> · last {when(o.crm_outbox?.last_sent_at)}</div>
        </Card>
        <Card title="Daily briefing" to="briefing">
          <div className="ia-big">{o.emails?.daily_briefing ? "On" : "Off"}</div>
          <div>{(o.emails?.recipients || []).join(", ") || "no recipients"} · threshold {o.emails?.threshold}</div>
          {o.emails?.weekly_newsletter_on_info_site && <div className="bad">The info site's own weekly newsletter is ON — subscribers will get two</div>}
        </Card>
        <Card title="Subscribers on info"><div className="ia-big">{o.subscribers?.active}</div><div>{o.subscribers?.unsubscribed} unsubscribed</div></Card>
      </div>
    </>
  );
}

/* ---------- 2. Partners (search profiles) ---------- */

interface Weighted { text: string; weight?: string }
const WEIGHTS = ["A", "B", "C", "D"];

function toWeighted(v: unknown[], key: string, extra: string): { items: Weighted[]; objects: boolean } {
  const objects = v.some(x => x && typeof x === "object");
  return {
    objects,
    items: v.map(x => (x && typeof x === "object" ? { text: String((x as Row)[key] ?? ""), weight: (x as Row)[extra] != null ? String((x as Row)[extra]) : undefined } : { text: String(x) })).filter(x => x.text),
  };
}

function Partners() {
  const { data, err, refetch } = useInfo<{ profiles: Row[] }>(`${BASE}/partners`);
  const [editing, setEditing] = useState<{ profile: Row; isNew: boolean; note?: string } | null>(null);
  const [suggestUrl, setSuggestUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const open = async (slug: string) => {
    setProblem(null);
    try { setEditing({ profile: (await api.get<{ profile: Row }>(`${BASE}/partners/${encodeURIComponent(slug)}`)).profile, isNew: false }); }
    catch (e) { setProblem(errText(e)); }
  };
  const suggest = async () => {
    setBusy(true); setProblem(null);
    try {
      const r = await api.post<{ profile: Row; source_url: string }>(`${BASE}/partners/suggest`, { url: suggestUrl });
      setEditing({ profile: { active: true, ...r.profile }, isNew: true, note: `AI draft from ${r.source_url} — review everything before saving.` });
    } catch (e) { setProblem(errText(e)); } finally { setBusy(false); }
  };

  if (editing) {
    return <ProfileForm key={editing.profile.slug || "new"} initial={editing.profile} isNew={editing.isNew} note={editing.note}
      onClose={() => setEditing(null)} onSaved={m => { setEditing(null); setMsg(m); refetch(); }} />;
  }
  return (
    <>
      <div className="ia-bar">
        <input className="ia-input" value={suggestUrl} onChange={e => setSuggestUrl(e.target.value)} placeholder="Partner website, e.g. https://www.fishbones.as" />
        <button className="btn primary sm" disabled={busy || !suggestUrl.trim()} onClick={suggest}>{busy ? "Reading the site… (up to a minute)" : "✦ Suggest a profile"}</button>
        <button className="btn ghost sm" onClick={() => setEditing({ profile: { active: true, product_phrases: [], exclusion_terms: [], target_countries: [], competitors: [], watched_projects: [] }, isNew: true })}>New, blank</button>
      </div>
      {msg && <div className="set-notice ok">{msg}</div>}
      <Problem err={problem || err} retry={refetch} />
      <table className="ia-table">
        <thead><tr><th>Partner</th><th>Phrases</th><th>Countries</th><th>Competitors</th><th>Projects</th><th>Updated</th></tr></thead>
        <tbody>
          {(data?.profiles || []).map(p => (
            <tr key={p.slug} className="click" onClick={() => open(p.slug)}>
              <td><b>{p.name}</b><div className="ia-sub">{p.description}</div></td>
              <td>{p.phrase_count}</td><td>{p.country_count}</td><td>{p.competitor_count}</td><td>{p.project_count}</td><td>{when(p.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function ProfileForm({ initial, isNew, note, onClose, onSaved }: {
  initial: Row; isNew: boolean; note?: string; onClose: () => void; onSaved: (msg: string) => void;
}) {
  const phrases0 = useMemo(() => toWeighted(initial.product_phrases || [], "phrase", "weight"), [initial]);
  const countries0 = useMemo(() => toWeighted(initial.target_countries || [], "country", "priority"), [initial]);
  const projects0 = useMemo(() => (initial.watched_projects || []).map((x: unknown) => (x && typeof x === "object" ? (x as Row) : { name: String(x) })), [initial]);
  const projectsAsObjects = (initial.watched_projects || []).some((x: unknown) => x && typeof x === "object");
  const [f, setF] = useState({
    slug: initial.slug || "", name: initial.name || "", description: initial.description || "", notes: initial.notes || "",
    phrases: phrases0.items, exclusions: (initial.exclusion_terms || []) as string[], countries: countries0.items,
    competitors: (initial.competitors || []) as string[], projects: projects0.map((p: Row) => String(p.name || "")).filter(Boolean) as string[],
    active: initial.active !== false,
  });
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const blocked = /bw\s*-?\s*offshore|saga\s*-?\s*energy\s*-?\s*m\s*-?\s*1\b/i.test(`${f.name} ${f.slug.replace(/[-_]/g, " ")}`);

  const build = (): Row => ({
    slug: f.slug.trim(), name: f.name.trim(), description: f.description, notes: f.notes, active: f.active,
    // Keep the format the profile already uses: objects if it had them (or a weight/priority was set), plain strings otherwise.
    product_phrases: phrases0.objects || f.phrases.some(p => p.weight) ? f.phrases.map(p => ({ phrase: p.text, ...(p.weight ? { weight: p.weight } : {}) })) : f.phrases.map(p => p.text),
    target_countries: countries0.objects || f.countries.some(c => c.weight && c.weight !== "1")
      ? f.countries.map(c => ({ country: c.text, priority: Number(c.weight || 1) })) : f.countries.map(c => c.text),
    exclusion_terms: f.exclusions, competitors: f.competitors,
    watched_projects: projectsAsObjects ? f.projects.map(n => projects0.find((p: Row) => p.name === n) || { name: n }) : f.projects,
  });
  const save = async () => {
    setBusy(true); setProblem(null);
    try {
      const body = build();
      if (isNew) {
        await api.post(`${BASE}/partners`, body);
        onSaved(`Created ${body.name}`);
      } else {
        // Send only what changed.
        const before: Row = { ...initial };
        const changed = Object.fromEntries(Object.entries(body).filter(([k, v]) => k !== "slug" && JSON.stringify(v) !== JSON.stringify(before[k] ?? (Array.isArray(v) ? [] : ""))));
        if (!Object.keys(changed).length) { onClose(); return; }
        await api.put(`${BASE}/partners/${encodeURIComponent(initial.slug)}`, changed);
        onSaved(`Saved ${body.name}`);
      }
    } catch (e) { setProblem(errText(e)); } finally { setBusy(false); }
  };
  const deactivate = async () => {
    if (!confirm(`Deactivate ${initial.name}? Its articles stop being attributed; the profile is kept.`)) return;
    try { await api.put(`${BASE}/partners/${encodeURIComponent(initial.slug)}`, { active: false }); onSaved(`Deactivated ${initial.name}`); }
    catch (e) { setProblem(errText(e)); }
  };
  const remove = async () => {
    if (!confirm(`Delete ${initial.name} for good? Deactivating keeps the profile and its history — prefer that.`)) return;
    try { await api.delete(`${BASE}/partners/${encodeURIComponent(initial.slug)}`); onSaved(`Deleted ${initial.name}`); }
    catch (e) { setProblem(errText(e)); }
  };

  return (
    <div className="set-form ia-form">
      <div className="ia-form-h"><b>{isNew ? "New partner profile" : `Edit ${initial.name}`}</b><button className="btn ghost sm" onClick={onClose}>Back to the list</button></div>
      {note && <div className="set-notice ia-note">{note}</div>}
      <div className="set-grid">
        <label className="set-field"><span className="set-label">Name *</span>
          <input value={f.name} onChange={e => setF({ ...f, name: e.target.value, ...(isNew && !f.slug ? {} : {}) })}
            onBlur={() => isNew && !f.slug && setF(x => ({ ...x, slug: x.name.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") }))} /></label>
        <label className="set-field"><span className="set-label">Slug *</span><input value={f.slug} disabled={!isNew} onChange={e => setF({ ...f, slug: e.target.value })} /></label>
        <label className="set-field wide"><span className="set-label">Description</span><input value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></label>
        <div className="set-field wide"><span className="set-label">Product phrases <span className="ia-hint">weight A (strongest) – D</span></span>
          <div className="ia-weighted">
            {f.phrases.map((p, i) => (
              <span key={p.text + i} className="ia-tag">
                {p.text}
                <select value={p.weight || ""} onChange={e => setF({ ...f, phrases: f.phrases.map((x, j) => (j === i ? { ...x, weight: e.target.value || undefined } : x)) })}>
                  <option value="">–</option>{WEIGHTS.map(w => <option key={w}>{w}</option>)}
                </select>
                <button aria-label={`Remove ${p.text}`} onClick={() => setF({ ...f, phrases: f.phrases.filter((_, j) => j !== i) })}>×</button>
              </span>
            ))}
            <TagInput value={[]} onChange={v => setF({ ...f, phrases: [...f.phrases, ...v.filter(t => !f.phrases.some(p => p.text === t)).map(text => ({ text }))] })} placeholder="Add a phrase" />
          </div></div>
        <div className="set-field wide"><span className="set-label">Target countries <span className="ia-hint">priority 1 (highest) – 5</span></span>
          <div className="ia-weighted">
            {f.countries.map((c, i) => (
              <span key={c.text + i} className="ia-tag">
                {c.text}
                <select value={c.weight || "1"} onChange={e => setF({ ...f, countries: f.countries.map((x, j) => (j === i ? { ...x, weight: e.target.value } : x)) })}>
                  {[1, 2, 3, 4, 5].map(n => <option key={n}>{n}</option>)}
                </select>
                <button aria-label={`Remove ${c.text}`} onClick={() => setF({ ...f, countries: f.countries.filter((_, j) => j !== i) })}>×</button>
              </span>
            ))}
            <TagInput value={[]} onChange={v => setF({ ...f, countries: [...f.countries, ...v.filter(t => !f.countries.some(c => c.text === t)).map(text => ({ text, weight: "1" }))] })} placeholder="Add a country" />
          </div></div>
        <div className="set-field wide"><span className="set-label">Exclusion terms</span><TagInput value={f.exclusions} onChange={v => setF({ ...f, exclusions: v })} /></div>
        <div className="set-field wide"><span className="set-label">Competitors</span><TagInput value={f.competitors} onChange={v => setF({ ...f, competitors: v })} /></div>
        <div className="set-field wide"><span className="set-label">Watched projects</span><TagInput value={f.projects} onChange={v => setF({ ...f, projects: v })} /></div>
        <label className="set-field wide"><span className="set-label">Notes</span><textarea rows={3} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></label>
      </div>
      {blocked && <div className="set-notice err">BW Offshore and Saga Energy M1 can't be set up as partners: BW Offshore asked on 13 October 2026 not to be named as Saga's partner.</div>}
      <Problem err={problem} />
      <div className="set-form-foot">
        {!isNew && initial.active !== false && <button className="btn ghost sm" onClick={deactivate}>Deactivate</button>}
        {!isNew && <button className="btn ghost sm set-danger" onClick={remove}>Delete…</button>}
        <span style={{ flex: 1 }} />
        <button className="btn ghost sm" onClick={onClose}>Cancel</button>
        <button className="btn primary sm" disabled={busy || blocked || !f.name.trim() || !f.slug.trim()} onClick={save}>{isNew ? "Create profile" : "Save"}</button>
      </div>
    </div>
  );
}

/* ---------- 3. Partner desk ---------- */

const INSTRUMENT: Record<string, string> = {
  contract_signed: "Contract signed", contract_ratified: "Contract ratified", tender: "Tender",
  mou_hoa: "MoU / heads of agreement", block_awarded: "Block awarded", reconnaissance_permit: "Reconnaissance permit",
  licence_extended: "Licence extended", licence_revoked: "Licence revoked",
};
const ACTIONABLE = ["contract_signed", "contract_ratified", "tender"];

function Desk() {
  const { data, err, refetch } = useInfo<{ partners: Row[]; threshold: number; attribution_last_run: string }>(`${BASE}/desk`);
  const [slug, setSlug] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [country, setCountry] = useState("");
  const [instrument, setInstrument] = useState("");
  const [minScore, setMinScore] = useState(0);
  const [q, setQ] = useState("");
  const [showRejected, setShowRejected] = useState(false);
  const [verdicts, setVerdicts] = useState<Record<string, string | null>>({});
  const feed = useInfo<{ articles: Row[]; total: number }>(slug ? `${BASE}/desk/${encodeURIComponent(slug)}?days=${days}&limit=200` : null);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => { if (!slug && data?.partners?.length) setSlug(data.partners[0].slug); }, [data, slug]);
  useEffect(() => { setVerdicts({}); }, [slug, days]);

  const items = (feed.data?.articles || []).filter(a => {
    const v = a.id in verdicts ? verdicts[a.id] : a.feedback?.verdict ?? a.feedback ?? null;
    if (v === "irrelevant" && !showRejected) return false;
    if (country && a.country !== country) return false;
    if (instrument === "actionable" ? !ACTIONABLE.includes(a.instrument) : instrument && a.instrument !== instrument) return false;
    if ((a.partner_score ?? a.score ?? 0) < minScore) return false;
    if (q && !`${a.title} ${a.snippet} ${(a.matched_terms || []).join(" ")}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const countries = [...new Set((feed.data?.articles || []).map(a => a.country).filter(Boolean))].sort();
  const mark = async (a: Row, verdict: "relevant" | "irrelevant" | null) => {
    setProblem(null);
    const prev = verdicts[a.id];
    setVerdicts(v => ({ ...v, [a.id]: verdict }));
    try {
      if (verdict) await api.put(`${BASE}/feedback/${encodeURIComponent(slug!)}/${encodeURIComponent(a.id)}`, { verdict });
      else await api.delete(`${BASE}/feedback/${encodeURIComponent(slug!)}/${encodeURIComponent(a.id)}`);
    } catch (e) { setVerdicts(v => ({ ...v, [a.id]: prev ?? null })); setProblem(errText(e)); }
  };

  if (err) return <Problem err={err} retry={refetch} />;
  return (
    <div className="ia-desk">
      <div className="ia-desk-list">
        {(data?.partners || []).map(p => (
          <button key={p.slug} className={clsx("ia-desk-p", slug === p.slug && "on")} onClick={() => setSlug(p.slug)}>
            <b>{p.name}</b>
            <span>{p.matches_7d} this week · {p.strong_7d} strong · {p.actionable_7d} actionable</span>
            <span className="ia-sub">precision {p.precision_90d == null ? "—" : `${Math.round(p.precision_90d * (p.precision_90d <= 1 ? 100 : 1))}%`} · {p.labelled_90d || 0} rated</span>
          </button>
        ))}
        {data && <div className="ia-sub" style={{ padding: 8 }}>Threshold {data.threshold} · attributed {when(data.attribution_last_run)}</div>}
      </div>
      <div className="ia-desk-feed">
        <div className="ia-bar">
          <select value={days} onChange={e => setDays(Number(e.target.value))}>{[7, 30, 90].map(d => <option key={d} value={d}>Last {d} days</option>)}</select>
          <select value={country} onChange={e => setCountry(e.target.value)}><option value="">All countries</option>{countries.map(c => <option key={c}>{c}</option>)}</select>
          <select value={instrument} onChange={e => setInstrument(e.target.value)}>
            <option value="">Any instrument</option><option value="actionable">Actionable only</option>
            {Object.entries(INSTRUMENT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="ia-inline">Min score <input type="number" min={0} max={100} value={minScore} onChange={e => setMinScore(Number(e.target.value) || 0)} /></label>
          <input className="ia-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" />
          <label className="ia-inline"><input type="checkbox" checked={showRejected} onChange={e => setShowRejected(e.target.checked)} /> Show ✗</label>
        </div>
        <Problem err={problem || feed.err} retry={feed.refetch} />
        {items.map(a => {
          const v = a.id in verdicts ? verdicts[a.id] : a.feedback?.verdict ?? a.feedback ?? null;
          return (
            <div key={a.id} className={clsx("ia-item", v === "irrelevant" && "rejected")}>
              <div className="ia-item-main">
                <a href={a.has_story ? `${INFO_PUBLIC}/story/${a.id}` : a.url} target="_blank" rel="noreferrer" className="ia-item-t">{a.title}</a>
                <div className="ia-sub">{[a.source_name, a.country, when(a.published_at), `score ${a.partner_score ?? a.score}`].filter(Boolean).join(" · ")}
                  {a.instrument && <> · <span className={clsx("ia-instr", ACTIONABLE.includes(a.instrument) && "act")}>{INSTRUMENT[a.instrument] || a.instrument}</span></>}</div>
                {a.snippet && <div className="ia-snip">{a.snippet}</div>}
                <div className="ia-chips">
                  {(a.matched_terms || []).map((t: string) => <span key={t} className="ia-chip">{t}</span>)}
                  {(a.watched_project_mentions || []).map((t: string) => <span key={t} className="ia-chip proj">◆ {t}</span>)}
                </div>
              </div>
              <div className="ia-verdict">
                <button className={clsx(v === "relevant" && "on ok")} title="Relevant" onClick={() => mark(a, v === "relevant" ? null : "relevant")}>✓</button>
                <button className={clsx(v === "irrelevant" && "on bad")} title="Not relevant" onClick={() => mark(a, v === "irrelevant" ? null : "irrelevant")}>✗</button>
              </div>
            </div>
          );
        })}
        {feed.data && !items.length && <div className="set-empty">Nothing matches.</div>}
      </div>
    </div>
  );
}

/* ---------- 5. Sources ---------- */

const CATEGORY_HELP: Record<string, string> = {
  noc_regulator: "Everything from this source is kept (no keyword filter).", government: "Everything is kept (no keyword filter).",
  tender_portal: "Everything is kept (no keyword filter).", "Regulators & NOCs": "Everything is kept (no keyword filter).",
  "Tenders & Procurement": "Everything is kept (no keyword filter).",
  news_agency: "A strict energy-word filter applies.", government_general: "A strict energy-word filter applies.",
  operator: "Without a home country, only items that mention Africa are kept.", contractor: "Without a home country, only items that mention Africa are kept.",
};
const CATEGORIES = ["noc_regulator", "government", "government_general", "tender_portal", "news_agency", "operator", "contractor", "industry_media", "Regulators & NOCs", "Tenders & Procurement"];

function Sources() {
  const { data, err, refetch } = useInfo<{ sources: Row[]; health: Row | null }>(`${BASE}/sources`);
  const sugg = useInfo<{ suggestions: Row[] }>(`${BASE}/sources/suggestions`);
  const [filter, setFilter] = useState("all");
  const [country, setCountry] = useState("");
  const [type, setType] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const rows = (data?.sources || []).filter(s0 =>
    (filter === "all" || (filter === "failing" ? s0.enabled && s0.health && !s0.health.healthy : !s0.enabled))
    && (!country || s0.home_country === country) && (!type || s0.type === type));
  const act = async (fn: () => Promise<unknown>, done: string) => {
    setProblem(null);
    try { await fn(); setMsg(done); refetch(); } catch (e) { setProblem(errText(e)); }
  };
  if (err) return <Problem err={err} retry={refetch} />;
  if (editing) {
    return <SourceForm initial={editing} onClose={() => setEditing(null)} onSaved={m => { setEditing(null); setMsg(m); refetch(); }} />;
  }
  return (
    <>
      {data?.health && <div className="ia-health ok">{data.health.healthy} of {data.health.enabled} healthy ({data.health.healthy_pct}%) · {data.health.sources_crawled_24h} crawled in 24 h · last crawl {when(data.health.last_crawl_at)}</div>}
      <div className="ia-bar">
        {["all", "failing", "disabled"].map(f => <button key={f} className={clsx("ia-pill", filter === f && "on")} onClick={() => setFilter(f)}>{f[0].toUpperCase() + f.slice(1)}</button>)}
        <select value={country} onChange={e => setCountry(e.target.value)}><option value="">All countries</option>
          {[...new Set((data?.sources || []).map(x => x.home_country).filter(Boolean))].sort().map(c => <option key={c}>{c}</option>)}</select>
        <select value={type} onChange={e => setType(e.target.value)}><option value="">All types</option>{["rss", "html", "html_links"].map(t => <option key={t}>{t}</option>)}</select>
        <span style={{ flex: 1 }} />
        <button className="btn primary sm" onClick={() => setEditing({ type: "rss", lang: "EN", tier: 3, crawl_frequency: "daily", enabled: true, _new: true })}>+ Add source</button>
      </div>
      {msg && <div className="set-notice ok">{msg}</div>}
      <Problem err={problem} />
      <table className="ia-table">
        <thead><tr><th /><th>Source</th><th>Type</th><th>Tier</th><th>Category</th><th>Health</th><th /></tr></thead>
        <tbody>
          {rows.map(s0 => {
            const h = s0.health;
            const failed = s0.enabled && h && !h.healthy;
            return (
              <tr key={s0.source_id} className={clsx(!s0.enabled && "off", failed && "failed")}>
                <td><span className={clsx("ia-dot", !s0.enabled ? "off" : failed ? "bad" : "ok")} /></td>
                <td><b>{s0.name}</b><div className="ia-sub"><a href={s0.url} target="_blank" rel="noreferrer">{s0.url}</a>{s0.home_country ? ` · ${s0.home_country}` : ""} · {s0.lang}</div></td>
                <td>{s0.type}</td><td>{s0.tier}</td><td>{s0.category}</td>
                <td className="ia-health-cell">{!s0.enabled ? "Disabled" : !h ? "—" : h.healthy ? `OK · ${h.new ?? 0} new, ${h.parsed_7d ?? 0} read this week` : (
                  <><b className="bad">{h.diagnosis || "Failing"}</b>{h.error && <div className="ia-sub bad">{h.error}{h.http_status ? ` (HTTP ${h.http_status})` : ""}</div>}</>)}</td>
                <td className="ia-row-acts">
                  <button className="ld-link" onClick={() => setEditing(s0)}>Edit</button>
                  <button className="ld-link" onClick={() => act(() => api.patch(`${BASE}/sources/${encodeURIComponent(s0.source_id)}`, { enabled: !s0.enabled }), `${s0.enabled ? "Disabled" : "Enabled"} ${s0.name}`)}>{s0.enabled ? "Disable" : "Enable"}</button>
                  <button className="ld-link bad" onClick={() => confirm(`Delete ${s0.name}? Disabling keeps its history — prefer that.`) && act(() => api.delete(`${BASE}/sources/${encodeURIComponent(s0.source_id)}`), `Deleted ${s0.name}`)}>Delete</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {(sugg.data?.suggestions || []).length > 0 && (
        <>
          <div className="section-label" style={{ margin: "18px 0 8px" }}>Suggested by the weekly discovery job</div>
          <table className="ia-table"><tbody>
            {(sugg.data!.suggestions).map((x, i) => (
              <tr key={x.url || i}><td><b>{x.name || x.url}</b><div className="ia-sub">{x.url}{x.reason ? ` — ${x.reason}` : ""}</div></td>
                <td className="ia-row-acts"><button className="ld-link" onClick={() => setEditing({ _new: true, type: x.type || "rss", lang: x.lang || "EN", tier: x.tier || 3, crawl_frequency: "daily",
                  name: x.name || "", url: x.url || "", category: x.category || "", home_country: x.home_country || "", source_id: x.source_id || "" })}>Add…</button></td></tr>
            ))}
          </tbody></table>
        </>
      )}
    </>
  );
}

function SourceForm({ initial, onClose, onSaved }: { initial: Row; onClose: () => void; onSaved: (m: string) => void }) {
  const isNew = !!initial._new;
  const [f, setF] = useState<Row>({ ...initial });
  const [problem, setProblem] = useState<string | null>(null);
  const set = (k: string, v: unknown) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    setProblem(null);
    const fields = ["name", "url", "type", "selector", "lang", "tier", "category", "crawl_frequency", "home_country", "weight", "enabled"];
    try {
      if (isNew) {
        const body: Row = { source_id: f.source_id };
        for (const k of fields) if (f[k] !== undefined && f[k] !== "") body[k] = k === "tier" || k === "weight" ? Number(f[k]) : f[k];
        await api.post(`${BASE}/sources`, body);
        onSaved(`Added ${f.name}`);
      } else {
        const changed: Row = {};
        for (const k of fields) if (JSON.stringify(f[k] ?? null) !== JSON.stringify(initial[k] ?? null)) changed[k] = k === "tier" || k === "weight" ? Number(f[k]) : f[k];
        if (!Object.keys(changed).length) { onClose(); return; }
        await api.patch(`${BASE}/sources/${encodeURIComponent(initial.source_id)}`, changed);
        onSaved(`Saved ${f.name}`);
      }
    } catch (e) { setProblem(errText(e)); }
  };
  return (
    <div className="set-form ia-form">
      <div className="ia-form-h"><b>{isNew ? "New source" : `Edit ${initial.name}`}</b><button className="btn ghost sm" onClick={onClose}>Back</button></div>
      <div className="set-grid">
        <label className="set-field"><span className="set-label">Source id *</span><input value={f.source_id || ""} disabled={!isNew} onChange={e => set("source_id", e.target.value)} placeholder="e.g. anpg_news" /></label>
        <label className="set-field"><span className="set-label">Name *</span><input value={f.name || ""} onChange={e => set("name", e.target.value)} /></label>
        <label className="set-field wide"><span className="set-label">URL *</span><input value={f.url || ""} onChange={e => set("url", e.target.value)} /></label>
        <label className="set-field"><span className="set-label">Type</span><select value={f.type} onChange={e => set("type", e.target.value)}>{["rss", "html", "html_links"].map(t => <option key={t}>{t}</option>)}</select></label>
        {f.type !== "rss" && <label className="set-field"><span className="set-label">CSS selector</span><input value={f.selector || ""} onChange={e => set("selector", e.target.value)} placeholder="e.g. article h2 a" /></label>}
        <label className="set-field"><span className="set-label">Language</span><select value={f.lang} onChange={e => set("lang", e.target.value)}>{["EN", "FR", "PT", "AR", "ES", "NO"].map(l => <option key={l}>{l}</option>)}</select></label>
        <label className="set-field"><span className="set-label">Tier (1 = most important)</span><select value={f.tier} onChange={e => set("tier", Number(e.target.value))}>{[1, 2, 3, 4, 5].map(n => <option key={n}>{n}</option>)}</select></label>
        <label className="set-field"><span className="set-label">Crawl</span><select value={f.crawl_frequency} onChange={e => set("crawl_frequency", e.target.value)}><option>daily</option><option>weekly</option></select></label>
        <label className="set-field"><span className="set-label">Home country</span><input value={f.home_country || ""} onChange={e => set("home_country", e.target.value)} /></label>
        <label className="set-field"><span className="set-label">Weight (0.1–3.0)</span><input type="number" step={0.1} min={0.1} max={3} value={f.weight ?? ""} onChange={e => set("weight", e.target.value)} /></label>
        <label className="set-field wide"><span className="set-label">Category</span>
          <select value={f.category || ""} onChange={e => set("category", e.target.value)}><option value="">—</option>
            {[...new Set([...CATEGORIES, f.category].filter(Boolean))].map(c => <option key={c}>{c}</option>)}</select>
          {f.category && <span className="set-hint">{CATEGORY_HELP[f.category] || "Standard keyword filtering applies."}</span>}
        </label>
      </div>
      <Problem err={problem} />
      <div className="set-form-foot"><span style={{ flex: 1 }} /><button className="btn ghost sm" onClick={onClose}>Cancel</button>
        <button className="btn primary sm" disabled={!f.source_id || !f.name || !f.url} onClick={save}>{isNew ? "Add source" : "Save"}</button></div>
    </div>
  );
}

/* ---------- 6. Review queue ---------- */

function Review() {
  const stats = useInfo<{ stats: { filter_reason: string; status: string; count: number }[] }>(`${BASE}/review/stats`);
  const reasons = [...new Set((stats.data?.stats || []).filter(s0 => s0.status === "pending").map(s0 => s0.filter_reason))];
  const [reason, setReason] = useState("");
  const [offset, setOffset] = useState(0);
  const list = useInfo<{ filtered: Row[]; total: number; limit: number }>(`${BASE}/review/filtered?status=pending&reason=${encodeURIComponent(reason)}&limit=50&offset=${offset}`);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => { setSel(new Set()); }, [reason, offset]);
  const count = (r: string) => (stats.data?.stats || []).filter(s0 => s0.status === "pending" && s0.filter_reason === r).reduce((n, s0) => n + s0.count, 0);
  const run = async (act: "approve" | "dismiss") => {
    setProblem(null);
    try {
      const r = await api.post<Row>(`${BASE}/review/${act}`, { ids: [...sel] });
      setMsg(act === "approve" ? `Published ${r.approved ?? sel.size}` : `Dismissed ${r.dismissed ?? sel.size}`);
      setSel(new Set()); list.refetch(); stats.refetch();
    } catch (e) { setProblem(errText(e)); }
  };
  const rows = list.data?.filtered || [];
  return (
    <>
      <div className="ia-bar">
        <button className={clsx("ia-pill", !reason && "on")} onClick={() => { setReason(""); setOffset(0); }}>All</button>
        {reasons.map(r => <button key={r} className={clsx("ia-pill", reason === r && "on")} onClick={() => { setReason(r); setOffset(0); }}>{r.replace(/_/g, " ")} <span className="tk-n">{count(r)}</span></button>)}
        <span style={{ flex: 1 }} />
        <button className="btn primary sm" disabled={!sel.size} onClick={() => run("approve")}>Approve {sel.size || ""}</button>
        <button className="btn ghost sm" disabled={!sel.size} onClick={() => run("dismiss")}>Dismiss {sel.size || ""}</button>
      </div>
      {msg && <div className="set-notice ok">{msg}</div>}
      <Problem err={problem || list.err || stats.err} retry={() => { list.refetch(); stats.refetch(); }} />
      <table className="ia-table">
        <thead><tr><th><input type="checkbox" checked={!!rows.length && rows.every(r => sel.has(r.id))}
          onChange={e => setSel(e.target.checked ? new Set(rows.map(r => r.id)) : new Set())} /></th><th>Article</th><th>Held back because</th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td><input type="checkbox" checked={sel.has(r.id)} onChange={e => { const n = new Set(sel); e.target.checked ? n.add(r.id) : n.delete(r.id); setSel(n); }} /></td>
              <td><a href={r.url} target="_blank" rel="noreferrer"><b>{r.title_en}</b></a><div className="ia-sub">{r.source_name} · {when(r.filtered_at)}</div>
                {r.summary_en && <div className="ia-snip">{r.summary_en}</div>}</td>
              <td>{String(r.filter_reason || "").replace(/_/g, " ")}{r.filter_detail && <div className="ia-sub">{r.filter_detail}</div>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {list.data && (
        <div className="ia-paging">
          <button className="btn ghost sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>← Previous</button>
          <span>{list.data.total ? `${offset + 1}–${Math.min(offset + rows.length, list.data.total)} of ${list.data.total}` : "Nothing waiting"}</span>
          <button className="btn ghost sm" disabled={offset + rows.length >= list.data.total} onClick={() => setOffset(offset + 50)}>Next →</button>
        </div>
      )}
    </>
  );
}

/* ---------- 7. Daily briefing ---------- */

function Briefing() {
  const { data, err, refetch } = useInfo<{ settings: Row; partners: { slug: string; name: string }[]; effective_recipients: string[]; effective_threshold: number; schedule: string }>(`${BASE}/notifications`);
  const [s0, setS] = useState<Row | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<Row | null>(null);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => { if (data) setS({ ...data.settings }); }, [data]);
  if (err) return <Problem err={err} retry={refetch} />;
  if (!data || !s0) return <div className="ia-loading">Reading the settings…</div>;
  const KEYS = ["daily_enabled", "daily_recipients", "daily_partners", "daily_threshold", "daily_actionable_only", "daily_include_source_health", "daily_send_when_empty", "source_alert_separate"];
  const changed = Object.fromEntries(KEYS.filter(k => JSON.stringify(s0[k]) !== JSON.stringify(data.settings[k])).map(k => [k, s0[k]]));
  const save = async () => {
    setProblem(null);
    try { await api.put(`${BASE}/notifications`, changed); setMsg("Saved"); refetch(); } catch (e) { setProblem(errText(e)); }
  };
  const check = (k: string, label: string) => (
    <label className="set-check"><input type="checkbox" checked={!!s0[k]} onChange={e => setS({ ...s0, [k]: e.target.checked })} /><span>{label}</span></label>
  );
  return (
    <div className="set-form ia-form">
      <div className="set-grid">
        <div className="set-field wide">{check("daily_enabled", "Send the daily briefing")}<span className="set-hint">{data.schedule}</span></div>
        <div className="set-field wide"><span className="set-label">Recipients</span>
          <TagInput value={s0.daily_recipients || []} onChange={v => setS({ ...s0, daily_recipients: v })} placeholder="name@saga-advisory.com" /></div>
        <div className="set-field wide"><span className="set-label">Partners in the briefing <span className="ia-hint">none ticked = all</span></span>
          <div className="fd-people">
            {data.partners.filter(p => !/bw\s*offshore|saga\s*energy\s*m1/i.test(p.name)).map(p => {
              const on = (s0.daily_partners || []).includes(p.slug);
              return <button key={p.slug} type="button" className={clsx("fd-person", on && "on")}
                onClick={() => setS({ ...s0, daily_partners: on ? s0.daily_partners.filter((x: string) => x !== p.slug) : [...(s0.daily_partners || []), p.slug] })}>{p.name}</button>;
            })}
          </div></div>
        <label className="set-field"><span className="set-label">Score threshold <span className="ia-hint">blank = default ({data.effective_threshold ?? 40})</span></span>
          <input type="number" min={0} max={100} value={s0.daily_threshold ?? ""} onChange={e => setS({ ...s0, daily_threshold: e.target.value === "" ? null : Number(e.target.value) })} /></label>
        <div className="set-field">
          {check("daily_actionable_only", "Only actionable items (contracts, tenders)")}
          {check("daily_include_source_health", "Include source health")}
          {check("daily_send_when_empty", "Send even when there's nothing new")}
          {check("source_alert_separate", "Source alerts as a separate email")}
        </div>
      </div>
      <div className="set-hint">The weekly newsletter is sent by the CRM, so the info site's own newsletter stays off.</div>
      <Problem err={problem} />
      {msg && <div className="set-notice ok">{msg}</div>}
      <div className="set-form-foot">
        <button className="btn ghost sm" disabled={busy === "preview"} onClick={async () => {
          setBusy("preview"); setProblem(null);
          try { setPreview(await api.get<Row>(`${BASE}/notifications/preview`)); } catch (e) { setProblem(errText(e)); } finally { setBusy(null); }
        }}>{busy === "preview" ? "Building…" : "Preview"}</button>
        <input className="ia-input" style={{ maxWidth: 240 }} value={testTo} onChange={e => setTestTo(e.target.value)} placeholder={`Test to (default ${(data.effective_recipients || [])[0] || "first recipient"})`} />
        <button className="btn ghost sm" disabled={busy === "test"} onClick={async () => {
          setBusy("test"); setProblem(null);
          try { const r = await api.post<Row>(`${BASE}/notifications/send-test`, testTo ? { to: testTo } : {}); setMsg(`Test sent to ${r.sent_to}: “${r.subject}”`); }
          catch (e) { setProblem(errText(e)); } finally { setBusy(null); }
        }}>{busy === "test" ? "Sending…" : "Send test"}</button>
        <span style={{ flex: 1 }} />
        <button className="btn ghost sm" disabled={!Object.keys(changed).length} onClick={() => setS({ ...data.settings })}>Reset</button>
        <button className="btn primary sm" disabled={!Object.keys(changed).length} onClick={save}>Save</button>
      </div>
      {preview && (
        <div className="ia-preview">
          <div className="ia-sub"><b>{preview.subject}</b> · {preview.items} item(s)</div>
          <iframe title="Briefing preview" sandbox="" srcDoc={preview.html} />
        </div>
      )}
    </div>
  );
}

/* ---------- 8. Crawler scope ---------- */

function Scope() {
  const { data, err, refetch } = useInfo<Row>(`${BASE}/scope`);
  const [f, setF] = useState<Row | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { if (data) setF({ countries: data.countries || [], sectors: data.sectors || [], keywords: data.keywords || [], min_score: data.min_score ?? 0 }); }, [data]);
  if (err) return <Problem err={err} retry={refetch} />;
  if (!data || !f) return <div className="ia-loading">Reading the scope…</div>;
  const changed = Object.fromEntries(Object.entries(f).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(data[k])));
  return (
    <div className="set-form ia-form">
      <div className="set-grid">
        <div className="set-field wide"><span className="set-label">Countries</span><TagInput value={f.countries} onChange={v => setF({ ...f, countries: v })} /></div>
        <div className="set-field wide"><span className="set-label">Sectors</span><TagInput value={f.sectors} onChange={v => setF({ ...f, sectors: v })} /></div>
        <div className="set-field wide"><span className="set-label">Keywords</span><TagInput value={f.keywords} onChange={v => setF({ ...f, keywords: v })} /></div>
        <label className="set-field"><span className="set-label">Minimum score (0–100)</span>
          <input type="number" min={0} max={100} value={f.min_score} onChange={e => setF({ ...f, min_score: Number(e.target.value) })} /></label>
      </div>
      <div className="set-hint">Last changed {when(data.updated_at)}.</div>
      <Problem err={problem} />
      {msg && <div className="set-notice ok">{msg}</div>}
      <div className="set-form-foot"><span style={{ flex: 1 }} />
        <button className="btn primary sm" disabled={!Object.keys(changed).length} onClick={async () => {
          setProblem(null);
          try { await api.post(`${BASE}/scope`, changed); setMsg("Saved"); refetch(); } catch (e) { setProblem(errText(e)); }
        }}>Save</button></div>
    </div>
  );
}

/* ---------- 9. Maintenance ---------- */

function Maintenance() {
  const [busy, setBusy] = useState<string | null>(null);
  const [out, setOut] = useState<Row | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const run = async (key: string, label: string, path: string, body: Row) => {
    if (!confirm(`${label}?`)) return;
    setBusy(key); setOut(null); setProblem(null);
    try { setOut(await api.post<Row>(`${BASE}/maintenance/${path}`, body)); } catch (e) { setProblem(errText(e)); } finally { setBusy(null); }
  };
  return (
    <div className="set-form ia-form">
      <div className="ia-maint">
        <div><b>Re-translate titles</b><div className="ia-sub">Fixes articles with missing or wrong English or Norwegian titles. Can take up to 5 minutes.</div></div>
        <button className="btn ghost sm" disabled={!!busy} onClick={() => run("rt", "Re-translate up to 500 articles", "retranslate", { limit: 500 })}>{busy === "rt" ? "Working… (up to 5 min)" : "Re-translate"}</button>
      </div>
      <div className="ia-maint">
        <div><b>Regenerate the weekly AI insights</b><div className="ia-sub">Up to 2 minutes.</div></div>
        <button className="btn ghost sm" disabled={!!busy} onClick={() => run("ri", "Regenerate the weekly insights", "regenerate-insights", {})}>{busy === "ri" ? "Working… (up to 2 min)" : "Regenerate"}</button>
      </div>
      <Problem err={problem} />
      {out && <pre className="ia-out">{String(out.stdout_tail ?? out.stdout ?? out.output ?? JSON.stringify(out, null, 2))}</pre>}
    </div>
  );
}
